(() => {
  if (window.vaRecipePresentation) {
    window.vaRecipePresentation.init(document);
    return;
  }
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const activeAnimations = new Set();
  const cancelAnimations = () => {
    activeAnimations.forEach((animation) => animation.cancel());
    activeAnimations.clear();
  };
  const motionAllowed = () => !motionPreference.matches && !document.documentElement.classList.contains('va-no-motion');
  const init = (container) => {
    const roots = [];
    if (container.matches?.('[data-va-recipe]')) roots.push(container);
    container.querySelectorAll?.('[data-va-recipe]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const controller = new AbortController();
      const signal = controller.signal;
      const ingredientInputs = Array.from(root.querySelectorAll('input[type="checkbox"][data-va-ingredient]'));
      const reset = root.querySelector('[data-va-ingredients-reset]');
      const status = root.querySelector('[data-va-ingredient-status]');
      const recipeId = root.dataset.recipeId || window.location.pathname;
      const storageKey = `va-recipe-ingredients:${recipeId}`;
      let observer = null;
      const ingredientKey = (input, index) => input.dataset.ingredientKey || input.id || String(index);
      const persistIngredients = () => {
        try {
          const checked = ingredientInputs.flatMap((input, index) => input.checked ? [ingredientKey(input, index)] : []);
          window.sessionStorage.setItem(storageKey, JSON.stringify(checked));
        } catch (error) {
          return;
        }
      };
      const updateIngredients = (persist = false) => {
        const checkedCount = ingredientInputs.filter((input) => input.checked).length;
        ingredientInputs.forEach((input) => input.closest('.va-ingredient')?.classList.toggle('is-checked', input.checked));
        if (reset) reset.disabled = checkedCount === 0;
        if (status?.dataset.statusTemplate) {
          status.textContent = status.dataset.statusTemplate.replace(/\{done\}/g, String(checkedCount)).replace(/\{total\}/g, String(ingredientInputs.length));
        }
        if (persist) persistIngredients();
      };
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(storageKey));
        if (Array.isArray(stored)) {
          ingredientInputs.forEach((input, index) => { input.checked = stored.includes(ingredientKey(input, index)); });
        }
      } catch (error) {
        ingredientInputs.forEach((input) => { input.checked = Boolean(input.checked); });
      }
      ingredientInputs.forEach((input) => {
        input.addEventListener('change', () => updateIngredients(true), { signal });
      });
      root.querySelectorAll('.va-ingredient').forEach((card) => {
        card.addEventListener('click', (event) => {
          if (event.target.closest('a, button, input, label, select, textarea')) return;
          const input = card.querySelector('input[type="checkbox"][data-va-ingredient]');
          if (input && !input.disabled) input.click();
        }, { signal });
      });
      if (reset && ingredientInputs.length) {
        reset.hidden = false;
        reset.addEventListener('click', () => {
          ingredientInputs.forEach((input) => { input.checked = false; });
          updateIngredients(true);
        }, { signal });
      }
      updateIngredients();
      root.querySelectorAll('[data-va-recipe-print]').forEach((button) => {
        if (typeof window.print !== 'function') return;
        button.hidden = false;
        button.addEventListener('click', () => window.print(), { signal });
      });
      const revealElements = Array.from(root.querySelectorAll('[data-va-recipe-reveal]'));
      if (motionAllowed() && 'IntersectionObserver' in window && typeof Element.prototype.animate === 'function' && revealElements.length) {
        let remaining = revealElements.length;
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            remaining -= 1;
            if (motionAllowed()) {
              const animation = entry.target.animate(
                [{ opacity: .74, transform: 'translateY(15px)' }, { opacity: 1, transform: 'translateY(0)' }],
                { duration: 550, easing: 'cubic-bezier(.2,.7,.2,1)' }
              );
              activeAnimations.add(animation);
              animation.finished.then(() => activeAnimations.delete(animation)).catch(() => activeAnimations.delete(animation));
            }
            if (remaining <= 0) observer.disconnect();
          });
        }, { threshold: .03, rootMargin: '0px 0px -12px 0px' });
        revealElements.forEach((element) => observer.observe(element));
      }
      instances.set(root, { controller, observer });
      root.dataset.recipeReady = 'true';
    });
  };
  motionPreference.addEventListener('change', () => {
    if (!motionPreference.matches) return;
    instances.forEach(({ observer }) => observer?.disconnect());
    cancelAnimations();
  });
  window.addEventListener('beforeprint', cancelAnimations);
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach(({ controller, observer }, root) => {
      if (event.target === root || event.target.contains(root)) {
        controller.abort();
        observer?.disconnect();
        instances.delete(root);
      }
    });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.vaRecipePresentation = { init };
  init(document);
})();
